import { InjectionToken } from '@angular/core';
import type { Observable } from 'rxjs';

export interface ReactiveFormBuilderEnvironmentFeatures {
  readonly filterInputParcelas?: boolean;
}

export interface ReactiveFormBuilderEnvironment {
  readonly appName: string;
  readonly production: boolean;
  readonly serverUrl: string;
  readonly features?: ReactiveFormBuilderEnvironmentFeatures;
}

export interface ReactiveFormBuilderStoredUser {
  readonly id?: string | number;
  readonly nombre?: string;
  readonly rol?: string;
}

export interface ReactiveFormBuilderStoredClient {
  readonly id?: string | number;
  readonly nombre?: string;
}

export interface ReactiveFormBuilderStorageAdapter {
  getUser(): ReactiveFormBuilderStoredUser | null;
  getClient(): ReactiveFormBuilderStoredClient | null;
}

export interface ReactiveFormBuilderFileUploadAdapter {
  uploadGeneralImage(file: File, folder: string): Observable<string>;
  uploadTaskFile(file: File, folder: string, database: string, field: string): Observable<string>;
}

export interface ReactiveFormBuilderDiagnosticScope {
  addMetric(name: string, value: number, unit?: string): void;
  end(metadata?: Record<string, unknown>): void;
  fail(error: unknown, metadata?: Record<string, unknown>): void;
}

export interface ReactiveFormBuilderDiagnosticsAdapter {
  start(name: string, group?: string, metadata?: Record<string, unknown>): ReactiveFormBuilderDiagnosticScope | null;
}

export interface ReactiveFormBuilderTourAdapter {
  startCurrentFormTour(): void;
}

export interface ReactiveFormBuilderBrowserAdapter {
  isChromiumBased(): boolean;
}

export interface ReactiveFormRequestData {
  readonly type?: unknown;
  readonly endpoint?: string;
  readonly params?: Record<string, unknown>;
  readonly validationNumber?: number;
}

export interface ReactiveFormRequestHandler<TResponse = unknown, TPayload = any> {
  readonly isLoading: boolean | undefined;
  readonly hasError: boolean | undefined;
  readonly hasErrorCliExists: boolean | undefined;
  readonly error?: unknown;
  safePerform(payload?: TPayload): void;
  response(callback: (response: TResponse) => void): void;
  fetchStatus?: (callback: (status: 'syncing' | 'finish' | 'error') => void) => void;
  unsuscribe(): void;
  getRequestData?: () => ReactiveFormRequestData;
  perform?: (payload?: TPayload) => unknown;
}

const DEFAULT_REACTIVE_FORM_BUILDER_ENVIRONMENT: ReactiveFormBuilderEnvironment = {
  appName: '',
  production: true,
  serverUrl: '',
  features: {
    filterInputParcelas: false,
  },
};

const DEFAULT_REACTIVE_FORM_BUILDER_STORAGE: ReactiveFormBuilderStorageAdapter = {
  getUser: () => null,
  getClient: () => null,
};

const DEFAULT_REACTIVE_FORM_BUILDER_FILE_UPLOAD: ReactiveFormBuilderFileUploadAdapter = {
  uploadGeneralImage: () => {
    throw new Error('ReactiveFormBuilder file upload adapter has not been provided.');
  },
  uploadTaskFile: () => {
    throw new Error('ReactiveFormBuilder file upload adapter has not been provided.');
  },
};

const DEFAULT_REACTIVE_FORM_BUILDER_DIAGNOSTICS: ReactiveFormBuilderDiagnosticsAdapter = {
  start: () => null,
};

const DEFAULT_REACTIVE_FORM_BUILDER_TOUR: ReactiveFormBuilderTourAdapter = {
  startCurrentFormTour: () => {},
};

const DEFAULT_REACTIVE_FORM_BUILDER_BROWSER: ReactiveFormBuilderBrowserAdapter = {
  isChromiumBased: () => false,
};

export const REACTIVE_FORM_BUILDER_ENVIRONMENT = new InjectionToken<ReactiveFormBuilderEnvironment>(
  'REACTIVE_FORM_BUILDER_ENVIRONMENT',
  {
    providedIn: 'root',
    factory: () => DEFAULT_REACTIVE_FORM_BUILDER_ENVIRONMENT,
  },
);

export const REACTIVE_FORM_BUILDER_STORAGE = new InjectionToken<ReactiveFormBuilderStorageAdapter>(
  'REACTIVE_FORM_BUILDER_STORAGE',
  {
    providedIn: 'root',
    factory: () => DEFAULT_REACTIVE_FORM_BUILDER_STORAGE,
  },
);

export const REACTIVE_FORM_BUILDER_FILE_UPLOAD = new InjectionToken<ReactiveFormBuilderFileUploadAdapter>(
  'REACTIVE_FORM_BUILDER_FILE_UPLOAD',
  {
    providedIn: 'root',
    factory: () => DEFAULT_REACTIVE_FORM_BUILDER_FILE_UPLOAD,
  },
);

export const REACTIVE_FORM_BUILDER_DIAGNOSTICS = new InjectionToken<ReactiveFormBuilderDiagnosticsAdapter>(
  'REACTIVE_FORM_BUILDER_DIAGNOSTICS',
  {
    providedIn: 'root',
    factory: () => DEFAULT_REACTIVE_FORM_BUILDER_DIAGNOSTICS,
  },
);

export const REACTIVE_FORM_BUILDER_TOUR = new InjectionToken<ReactiveFormBuilderTourAdapter>(
  'REACTIVE_FORM_BUILDER_TOUR',
  {
    providedIn: 'root',
    factory: () => DEFAULT_REACTIVE_FORM_BUILDER_TOUR,
  },
);

export const REACTIVE_FORM_BUILDER_BROWSER = new InjectionToken<ReactiveFormBuilderBrowserAdapter>(
  'REACTIVE_FORM_BUILDER_BROWSER',
  {
    providedIn: 'root',
    factory: () => DEFAULT_REACTIVE_FORM_BUILDER_BROWSER,
  },
);

export const REACTIVE_FORM_NOOP_REQUEST_HANDLER: ReactiveFormRequestHandler = {
  isLoading: false,
  hasError: false,
  hasErrorCliExists: false,
  safePerform: () => {},
  response: () => {},
  unsuscribe: () => {},
};

export * from './reactive-form-request.adapter';
